import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { cn } from '@/lib/cn';

import { ICONS, type IconName } from './icons';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Required: an icon-only control without an accessible name is invisible to a screen reader. */
  label: string;
  /**
   * `danger` er slett: grå som naboen i hvile, rød først ved hover eller fokus (§16.5 punkt 2).
   * En kolonne med tjue røde bøtter er tjue måter å ødelegge noe på, og det er det øyet lander på.
   */
  tone?: 'default' | 'danger';
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Et ikon som er en handling — `ghost` uten tekst (Glass 2, §16.5 punkt 1 og 2).
 *
 * Grått ikon, ingen ramme, ingen flate i hvile; tonalt fyll ved hover. Alltid synlig, også på en
 * rad man ikke peker på, fordi Tvitvi skal brukes på berøringsskjerm, der det ikke finnes noen hover
 * som kan hente det fram.
 *
 * `label` går til både `aria-label` og `title`, så tooltipen og det tilgjengelige navnet er det
 * samme ordet — to tekster som skal si det samme er ett sted å si det forskjellig.
 *
 * 32 px tegnet, 44 px å treffe (`hit-target`, DESIGN.md §9).
 */
export function IconButton({
  icon,
  label,
  tone = 'default',
  className,
  ...rest
}: IconButtonProps): ReactNode {
  const Icon = ICONS[icon];
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={cn(
        'hit-target',
        'inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-transparent',
        'bg-transparent text-muted ring-focus',
        'transition-colors duration-[var(--duration-fast)]',
        tone === 'danger'
          ? 'hover:bg-danger-subtle hover:text-danger focus-visible:text-danger'
          : 'hover:bg-[var(--fill-tonal)] hover:text-default',
        'disabled:pointer-events-none disabled:opacity-50',
        className,
      )}
      {...rest}
    >
      <Icon aria-hidden className="size-4" />
    </button>
  );
}
